/**
 * CustomerPricing.jsx (iter 142)
 *
 * 4-tier customer price-list editor. Tenants who have the customer_pricing
 * add-on ($149/mo) or the revenue_ops_bundle ($399/mo) see this page;
 * everyone else sees the upgrade-required paywall via FeatureGate at
 * the App.js route level.
 *
 * Layout:
 *   • TIER CONFIG CARD — name / mode (discount or cost_plus) / value % / currency
 *   • MARGIN-FLOOR CONFIG ROW — min_margin_pct + block/warn enforcement
 *   • CUSTOMER ASSIGN ROW — bulk-edit which tier a customer email is on
 *   • PRICE MATRIX — paginated table, every SKU × 4 tier prices with margin
 *     chips. Click any cell to set a manual override; clear to revert.
 *
 * iter142 refactor: BulkDialog, FloorBreachDialog, AssignDialog extracted
 * to /components/pricing/*. DialogDescription added to all dialogs.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tag, FloppyDisk, ArrowClockwise, Lock, LockOpen,
  CaretLeft, CaretRight, MagnifyingGlass, Users, Money,
  Shield, Lightning,
} from '@phosphor-icons/react';
import { BulkDialog } from '../components/pricing/BulkDialog';
import { FloorBreachDialog } from '../components/pricing/FloorBreachDialog';
import { AssignDialog } from '../components/pricing/AssignDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const fmtMoney = (n, currency = 'USD') => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  try {
    return Number(n).toLocaleString('en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
};

// Color-coded margin chip — green ≥30, amber ≥10, rose <10, slate when null/0
const marginCls = (pct) => {
  if (pct === null || pct === undefined) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (pct >= 30) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (pct >= 10) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
};

export default function CustomerPricing() {
  const [config, setConfig] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);

  // Inline override editor state
  const [editingCell, setEditingCell] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const [floorBreachConfirm, setFloorBreachConfirm] = useState(null); // {detail} | null

  // Dialog open states (form state lives inside each dialog component)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignedList, setAssignedList] = useState([]);
  const [categories, setCategories] = useState([]);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/api/customer-pricing/config`, { withCredentials: true });
      setConfig(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setError('upgrade_required');
      } else {
        setError(e?.response?.data?.detail || 'Could not load tier config');
      }
    }
  };

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/customer-pricing/matrix`, {
        params: { page, page_size: PAGE_SIZE, search: search || undefined },
        withCredentials: true,
      });
      setMatrix(r.data);
    } catch (e) {
      if (e?.response?.status === 402) setError('upgrade_required');
    } finally {
      setLoading(false);
    }
  };

  const loadAssigned = async () => {
    try {
      const r = await axios.get(`${API}/api/customer-pricing/customers`, { withCredentials: true });
      setAssignedList(r.data || []);
    } catch { /* swallow */ }
  };

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/api/customer-pricing/categories`, { withCredentials: true });
      setCategories(r.data || []);
    } catch { /* swallow */ }
  };

  useEffect(() => { loadConfig(); loadAssigned(); loadCategories(); }, []);
  useEffect(() => { if (config) loadMatrix(); /* eslint-disable-next-line */ }, [page, config]);

  const refreshAll = () => { setPage(1); loadConfig(); loadMatrix(); loadAssigned(); };

  // ---- Tier config edit handlers ----
  const updateTier = (idx, patch) => {
    setConfig((c) => ({
      ...c,
      tiers: c.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    try {
      const payload = {
        tiers: config.tiers.map((t) => ({ ...t, value: parseFloat(t.value) || 0 })),
        min_margin_pct: config.min_margin_pct === '' || config.min_margin_pct == null
          ? null
          : parseFloat(config.min_margin_pct),
        floor_enforcement: config.floor_enforcement || 'block',
      };
      const r = await axios.put(`${API}/api/customer-pricing/config`, payload, { withCredentials: true });
      setConfig(r.data);
      toast.success('Tier config saved · all SKU prices recalculated');
      await loadMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save config');
    } finally {
      setSavingConfig(false);
    }
  };

  // Sub-dialog callback handlers
  const runBulk = async (payload) => {
    const r = await axios.post(`${API}/api/customer-pricing/bulk-override`, payload, { withCredentials: true });
    return r.data;
  };

  const assignCustomer = async (email, tier_key) => {
    await axios.put(`${API}/api/customer-pricing/customer/${encodeURIComponent(email)}`, {
      tier_key,
    }, { withCredentials: true });
    await loadAssigned();
  };

  // ---- Override cell handlers ----
  const openCellEditor = (row, tier) => {
    setEditingCell({
      sku_id: row.sku_id, tier_key: tier.tier_key,
      sku_code: row.sku_code,
      tier_name: matrix.tiers.find((t) => t.key === tier.tier_key)?.name || tier.tier_key,
      current_price: tier.price,
      is_override: tier.is_override,
      cost: row.landed_cost,
    });
    setEditPrice(String(tier.price));
  };

  const saveOverride = async (force = false) => {
    if (!editingCell) return;
    const num = parseFloat(editPrice);
    if (Number.isNaN(num) || num < 0) {
      toast.error('Enter a non-negative price.');
      return;
    }
    setSavingCell(true);
    try {
      await axios.put(`${API}/api/customer-pricing/override`, {
        sku_id: editingCell.sku_id,
        tier_key: editingCell.tier_key,
        price: num,
        force,
      }, { withCredentials: true });
      toast.success('Override saved');
      setEditingCell(null);
      setFloorBreachConfirm(null);
      await loadMatrix();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail && detail.code === 'margin_floor_breached') {
        // Surface a confirm dialog with the structured breach payload.
        setFloorBreachConfirm(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not save override');
      }
    } finally {
      setSavingCell(false);
    }
  };

  const clearOverride = async () => {
    if (!editingCell) return;
    setSavingCell(true);
    try {
      await axios.delete(
        `${API}/api/customer-pricing/override/${editingCell.sku_id}/${editingCell.tier_key}`,
        { withCredentials: true },
      );
      toast.success('Override cleared · auto price restored');
      setEditingCell(null);
      await loadMatrix();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not clear override');
    } finally {
      setSavingCell(false);
    }
  };

  // ---- UI ----
  if (error === 'upgrade_required') {
    return (
      <Layout>
        <div className="p-12 text-center max-w-lg mx-auto" data-testid="customer-pricing-paywall">
          <Lock size={48} className="text-amber-600 mx-auto mb-4" weight="fill" />
          <h1 className="text-2xl font-bold mb-2">Custom Customer Price List</h1>
          <p className="text-sm text-muted-foreground mb-6">
            This module is part of the <b>Custom Customer Price List (A$149/mo)</b> add-on or the
            <b> Revenue Operations Suite bundle (A$399/mo)</b>. Activate it from billing to start
            using 4-tier pricing across all SKUs.
          </p>
          <a href="/settings/billing/addons" data-testid="customer-pricing-upgrade-btn">
            <Button className="bg-brand hover:bg-brand/90 text-white rounded-none">View add-ons</Button>
          </a>
        </div>
      </Layout>
    );
  }

  const totalPages = matrix ? Math.max(1, Math.ceil(matrix.total / PAGE_SIZE)) : 1;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="customer-pricing-page">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Tag size={28} weight="fill" className="text-brand" /> Customer Pricing
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Four customer tiers · auto-price every SKU · override per cell · live margin display.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/reports/margin-floor-audit'} className="rounded-none" data-testid="open-floor-audit">
              <Shield size={14} className="mr-2" weight="fill" /> Floor audit
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="rounded-none" data-testid="open-bulk-dialog">
              <Lightning size={14} className="mr-2" weight="fill" /> Bulk edit
            </Button>
            <Button variant="outline" onClick={() => setAssignOpen(true)} className="rounded-none" data-testid="open-assign-dialog">
              <Users size={14} className="mr-2" /> Assign customers ({assignedList.length})
            </Button>
            <Button variant="outline" onClick={refreshAll} className="rounded-none" data-testid="refresh-pricing">
              <ArrowClockwise size={14} className="mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Tier Config card */}
        {config && (
          <div className="bg-card border border-border" data-testid="tier-config-card">
            <div className="overline px-4 py-3 border-b border-border flex items-center justify-between">
              <span>Tier configuration · 4 customer tiers</span>
              <Button
                size="sm"
                onClick={saveConfig}
                disabled={savingConfig}
                className="bg-brand hover:bg-brand/90 text-white rounded-none h-7 text-xs"
                data-testid="save-tier-config"
              >
                <FloppyDisk size={12} className="mr-1" weight="fill" /> {savingConfig ? 'Saving…' : 'Save tiers'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4">
              {config.tiers.map((t, idx) => (
                <div key={t.key} className="p-4 border-r border-border last:border-r-0 space-y-3" data-testid={`tier-card-${t.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t.key.toUpperCase()}</div>
                  </div>
                  <Input
                    value={t.name}
                    onChange={(e) => updateTier(idx, { name: e.target.value })}
                    placeholder="Tier name"
                    className="font-bold text-lg h-9"
                    data-testid={`tier-name-${t.key}`}
                  />
                  <div>
                    <Label className="overline">Mode</Label>
                    <select
                      value={t.mode}
                      onChange={(e) => updateTier(idx, { mode: e.target.value })}
                      className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                      data-testid={`tier-mode-${t.key}`}
                    >
                      <option value="discount">Discount off list</option>
                      <option value="cost_plus">Cost-plus markup</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="overline">{t.mode === 'discount' ? 'Discount %' : 'Markup %'}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={t.value}
                        onChange={(e) => updateTier(idx, { value: e.target.value })}
                        className="mt-1 tabular-nums"
                        data-testid={`tier-value-${t.key}`}
                      />
                    </div>
                    <div>
                      <Label className="overline">Currency</Label>
                      <Input
                        value={t.currency}
                        onChange={(e) => updateTier(idx, { currency: e.target.value.toUpperCase().slice(0, 3) })}
                        maxLength={3}
                        className="mt-1 uppercase font-mono"
                        data-testid={`tier-ccy-${t.key}`}
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground italic leading-tight">
                    {t.mode === 'discount'
                      ? `Auto-prices = list × (1 − ${t.value || 0}%)`
                      : `Auto-prices = landed_cost × (1 + ${t.value || 0}%)`}
                  </p>
                </div>
              ))}
            </div>
            {/* Margin-floor protection row */}
            <div className="bg-amber-50/40 border-t border-border px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto] gap-3 items-center" data-testid="margin-floor-config">
              <div className="flex items-start gap-2">
                <Shield size={18} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold text-sm">Margin floor protection</div>
                  <p className="text-xs text-muted-foreground leading-tight">
                    Any tier price (auto or override) below this margin % is {config.floor_enforcement === 'warn' ? 'flagged' : 'blocked'}. Leave blank to disable.
                  </p>
                </div>
              </div>
              <div>
                <Label className="overline" htmlFor="min-margin-input">Minimum margin %</Label>
                <Input
                  id="min-margin-input"
                  type="number"
                  step="0.5"
                  min="0"
                  max="99"
                  value={config.min_margin_pct ?? ''}
                  onChange={(e) => setConfig({ ...config, min_margin_pct: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  placeholder="e.g. 15"
                  className="mt-1 tabular-nums"
                  data-testid="min-margin-input"
                />
              </div>
              <div>
                <Label className="overline" htmlFor="floor-enforcement-select">When breached</Label>
                <select
                  id="floor-enforcement-select"
                  value={config.floor_enforcement || 'block'}
                  onChange={(e) => setConfig({ ...config, floor_enforcement: e.target.value })}
                  className="mt-1 w-full text-sm px-3 py-2 border border-border bg-background"
                  data-testid="floor-enforcement-select"
                >
                  <option value="block">Block (require confirm)</option>
                  <option value="warn">Warn only (allow)</option>
                </select>
              </div>
              <div className="text-[11px] text-muted-foreground italic md:text-right">
                {config.min_margin_pct == null
                  ? 'No floor configured'
                  : `${config.min_margin_pct}% floor · ${config.floor_enforcement === 'warn' ? 'warn mode' : 'block mode'}`}
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); loadMatrix(); } }}
              placeholder="Search by SKU code, name, or barcode…"
              className="pl-8"
              data-testid="pricing-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setPage(1); loadMatrix(); }} className="rounded-none" data-testid="pricing-search-go">Search</Button>
          <div className="text-xs text-muted-foreground font-mono ml-auto">
            {matrix ? `${matrix.total} SKUs · page ${page}/${totalPages}` : '…'}
          </div>
        </div>

        {/* Matrix table */}
        <div className="bg-card border border-border overflow-x-auto" data-testid="pricing-matrix">
          {loading || !matrix ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
          ) : matrix.rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="pricing-empty">
              No SKUs match.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3 sticky left-0 bg-muted">SKU</th>
                  <th className="text-right p-3">Cost</th>
                  <th className="text-right p-3">List</th>
                  {matrix.tiers.map((t) => (
                    <th key={t.key} className="text-center p-3" colSpan={1}>
                      <div className="font-bold">{t.name}</div>
                      <div className="text-[10px] font-normal normal-case text-muted-foreground">
                        {t.mode === 'discount' ? `−${t.value}%` : `+${t.value}%`}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((row) => (
                  <tr key={row.sku_id} className="border-t border-border hover:bg-muted/30" data-testid={`pricing-row-${row.sku_code}`}>
                    <td className="p-3 sticky left-0 bg-card hover:bg-muted/30">
                      <div className="font-mono text-xs font-bold">{row.sku_code}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-[200px]">{row.name}</div>
                    </td>
                    <td className="p-3 text-right tabular-nums font-mono text-xs text-muted-foreground">
                      {fmtMoney(row.landed_cost)}
                    </td>
                    <td className="p-3 text-right tabular-nums font-mono text-xs">
                      {row.list_price ? fmtMoney(row.list_price) : '—'}
                    </td>
                    {row.tiers.map((t) => {
                      const tDef = matrix.tiers.find((x) => x.key === t.tier_key);
                      return (
                        <td
                          key={t.tier_key}
                          className="p-2 text-center cursor-pointer hover:bg-brand/5"
                          onClick={() => openCellEditor(row, t)}
                          data-testid={`cell-${row.sku_code}-${t.tier_key}`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`font-bold tabular-nums ${t.is_override ? 'text-brand' : ''}`}>
                              {fmtMoney(t.price, tDef?.currency || 'USD')}
                              {t.is_override && <Lock size={9} weight="fill" className="inline ml-1 text-brand" />}
                            </span>
                            <span className={`text-[10px] px-1.5 border tabular-nums ${marginCls(t.margin_pct)}`}>
                              {t.margin_pct !== null ? `${t.margin_pct}%` : '—'}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {matrix && matrix.total > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-none" data-testid="pricing-prev-page">
              <CaretLeft size={12} /> Prev
            </Button>
            <span className="text-xs font-mono">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={!matrix.has_more} onClick={() => setPage(page + 1)} className="rounded-none" data-testid="pricing-next-page">
              Next <CaretRight size={12} />
            </Button>
          </div>
        )}

        <p className="text-[11px] text-muted-foreground font-mono">
          Click any cell to override its price. Cleared overrides revert to the tier formula.
        </p>
      </div>

      {/* Cell-override dialog (kept inline because it depends on editingCell state) */}
      <Dialog open={!!editingCell} onOpenChange={(v) => { if (!v) setEditingCell(null); }}>
        <DialogContent className="border border-border rounded-none" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">
              {editingCell?.sku_code} · {editingCell?.tier_name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Set a manual price for this SKU at this tier. Clearing the override reverts to the formula-driven auto price.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Landed cost</span><span className="font-mono">{fmtMoney(editingCell?.cost)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Current tier price</span><span className="font-mono">{fmtMoney(editingCell?.current_price)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span>
                <span className="font-mono">{editingCell?.is_override ? '🔒 manual override' : 'auto-computed'}</span>
              </div>
            </div>
            <div>
              <Label className="overline" htmlFor="override-price">Override price</Label>
              <Input
                id="override-price"
                type="number"
                step="0.01"
                min="0"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                className="mt-1 tabular-nums font-mono"
                data-testid="override-price-input"
                autoFocus
              />
            </div>
            {editPrice && editingCell && (
              <div className="text-xs text-muted-foreground" data-testid="override-margin-preview">
                <Money size={11} weight="fill" className="inline mr-1" />
                Margin at this price: <b className="font-mono">
                  {parseFloat(editPrice) > 0
                    ? `${(((parseFloat(editPrice) - editingCell.cost) / parseFloat(editPrice)) * 100).toFixed(1)}%`
                    : '—'}
                </b>
              </div>
            )}
            <div className="flex gap-2">
              {editingCell?.is_override && (
                <Button
                  variant="outline"
                  onClick={clearOverride}
                  disabled={savingCell}
                  className="rounded-none text-rose-700 border-rose-200 hover:bg-rose-50"
                  data-testid="override-clear-btn"
                >
                  <LockOpen size={14} className="mr-1" /> Revert to auto
                </Button>
              )}
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={() => setEditingCell(null)}
                disabled={savingCell}
                className="rounded-none"
                data-testid="override-cancel-btn"
              >Cancel</Button>
              <Button
                onClick={() => saveOverride(false)}
                disabled={savingCell}
                className="bg-brand hover:bg-brand/90 text-white rounded-none"
                data-testid="override-save-btn"
              >{savingCell ? 'Saving…' : 'Save override'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extracted sub-dialogs */}
      <BulkDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        tiers={config?.tiers || []}
        categories={categories}
        onRun={runBulk}
        onApplied={loadMatrix}
      />

      <FloorBreachDialog
        breach={floorBreachConfirm}
        onCancel={() => setFloorBreachConfirm(null)}
        onForce={() => saveOverride(true)}
        busy={savingCell}
      />

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        tiers={config?.tiers || []}
        assignedList={assignedList}
        onAssign={assignCustomer}
      />
    </Layout>
  );
}
